import type { StructuredAssistantArtifact } from './structured';

const CRC_TABLE = new Uint32Array(256).map((_, index) => {
  let crc = index;
  for (let bit = 0; bit < 8; bit += 1) crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
  return crc >>> 0;
});

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const value of bytes) crc = CRC_TABLE[(crc ^ value) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

function u16(value: number): Uint8Array {
  const bytes = new Uint8Array(2);
  bytes[0] = value & 0xff;
  bytes[1] = (value >>> 8) & 0xff;
  return bytes;
}

function u32(value: number): Uint8Array {
  const bytes = new Uint8Array(4);
  bytes[0] = value & 0xff;
  bytes[1] = (value >>> 8) & 0xff;
  bytes[2] = (value >>> 16) & 0xff;
  bytes[3] = (value >>> 24) & 0xff;
  return bytes;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildStoredZip(files: Array<{ path: string; content: string }>): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.path);
    const data = encoder.encode(file.content);
    const checksum = crc32(data);
    const local = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(checksum),
      u32(data.length),
      u32(data.length),
      u16(name.length),
      u16(0),
      name,
      data,
    ]);
    const central = concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(checksum),
      u32(data.length),
      u32(data.length),
      u16(name.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      name,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }
  const centralDirectory = concatBytes(centrals);
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDirectory.length),
    u32(offset),
    u16(0),
  ]);
  return concatBytes([...locals, centralDirectory, end]);
}

function paragraph(text: string, style?: 'heading1' | 'heading2' | 'heading3'): string {
  const run = `<w:r><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
  if (!style) return `<w:p>${run}</w:p>`;
  return `<w:p><w:pPr><w:pStyle w:val="${style}"/></w:pPr>${run}</w:p>`;
}

export function documentArtifactToDocx(artifact: Extract<StructuredAssistantArtifact, { artifactType: 'scarlet.document.v1' }>): Uint8Array {
  const body: string[] = [paragraph(artifact.title, 'heading1'), paragraph(artifact.summary)];
  for (const block of artifact.blocks) {
    if (block.type === 'heading') body.push(paragraph(block.text, block.level === 1 ? 'heading1' : block.level === 2 ? 'heading2' : 'heading3'));
    if (block.type === 'paragraph') body.push(paragraph(block.text));
    if (block.type === 'list') {
      body.push(...block.items.map((item, index) => paragraph(block.style === 'numbered' ? `${index + 1}. ${item}` : `• ${item}`)));
    }
    if (block.type === 'table') {
      const header = `<w:tr>${block.columns.map((column) => `<w:tc><w:p><w:r><w:t>${escapeXml(column)}</w:t></w:r></w:p></w:tc>`).join('')}</w:tr>`;
      const rows = block.rows.map((row) => `<w:tr>${block.columns.map((_, index) => `<w:tc><w:p><w:r><w:t>${escapeXml(String(row[index] ?? ''))}</w:t></w:r></w:p></w:tc>`).join('')}</w:tr>`).join('');
      body.push(`<w:tbl><w:tblPr><w:tblW w:w="5000" w:type="pct"/></w:tblPr>${header}${rows}</w:tbl>`);
    }
  }
  if (artifact.citations.length) {
    body.push(paragraph('Sources', 'heading2'));
    body.push(...artifact.citations.map((citation) => paragraph(`${citation.label}: ${citation.url}`)));
  }
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${body.join('')}<w:sectPr/></w:body></w:document>`;
  return buildStoredZip([
    {
      path: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`,
    },
    {
      path: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`,
    },
    { path: 'word/document.xml', content: documentXml },
    {
      path: 'word/_rels/document.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`,
    },
  ]);
}

function columnLetter(index: number): string {
  let value = index + 1;
  let label = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

function inlineCell(reference: string, text: string): string {
  return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

function formulaCell(reference: string, formula: string): string {
  return `<c r="${reference}"><f>${escapeXml(formula)}</f></c>`;
}

export function spreadsheetArtifactToXlsx(artifact: Extract<StructuredAssistantArtifact, { artifactType: 'scarlet.spreadsheet.v1' }>): Uint8Array {
  const sheetFiles = artifact.sheets.map((sheet, sheetIndex) => {
    const header = `<row r="1">${sheet.columns.map((column, columnIndex) => inlineCell(`${columnLetter(columnIndex)}1`, column.label)).join('')}</row>`;
    const rows = sheet.rows.map((row, rowIndex) => {
      const excelRow = rowIndex + 2;
      const cells = sheet.columns.map((column, columnIndex) => {
        const reference = `${columnLetter(columnIndex)}${excelRow}`;
        const cell = row[column.key];
        if (cell && typeof cell === 'object' && 'formula' in cell && typeof cell.formula === 'string') return formulaCell(reference, cell.formula);
        const value = cell && typeof cell === 'object' && 'value' in cell ? cell.value : '';
        return inlineCell(reference, value === null || value === undefined ? '' : String(value));
      });
      return `<row r="${excelRow}">${cells.join('')}</row>`;
    }).join('');
    return {
      path: `xl/worksheets/sheet${sheetIndex + 1}.xml`,
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${header}${rows}</sheetData></worksheet>`,
    };
  });
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${artifact.sheets.map((sheet, index) => `<sheet name="${escapeXml(sheet.name.slice(0, 31))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join('')}</sheets></workbook>`;
  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${artifact.sheets.map((_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join('')}</Relationships>`;
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${artifact.sheets.map((_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`;
  return buildStoredZip([
    { path: '[Content_Types].xml', content: contentTypes },
    {
      path: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    { path: 'xl/workbook.xml', content: workbook },
    { path: 'xl/_rels/workbook.xml.rels', content: workbookRels },
    ...sheetFiles,
  ]);
}
